import type OpenAI from 'openai';

import { AI_ERROR_CODES, type CommonResponse } from 'src/types';
import axios, { ENDPOINTS } from 'src/utils/axios';
import { isErrorResponse } from 'src/utils/response-type-guards';

import { trackPrivacyResidualRedacted } from '../analytics';
import { LLM_TEMPERATURE } from '../config';
import { redactResidual } from '../privacy/backstop';

export type ChatMessage = OpenAI.Chat.Completions.ChatCompletionMessageParam;
export type ChatTool = OpenAI.Chat.Completions.ChatCompletionTool;
export type ChatToolCall = OpenAI.Chat.Completions.ChatCompletionMessageToolCall;
export type AssistantMessage = OpenAI.Chat.Completions.ChatCompletionMessage;

type ChatCompletion = OpenAI.Chat.Completions.ChatCompletion;

/** Token accounting for a single completion, when the provider reports it. */
export type ChatUsage = {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
};

export type ChatCompletionResult = {
  message: AssistantMessage;
  usage: ChatUsage | null;
};

export class ContextOverflowError extends Error {
  constructor(message = 'The conversation exceeded the model context window') {
    super(message);
    this.name = 'ContextOverflowError';
  }
}

const toUsage = (usage: ChatCompletion['usage']): ChatUsage | null =>
  usage
    ? {
        promptTokens: usage.prompt_tokens,
        completionTokens: usage.completion_tokens,
        totalTokens: usage.total_tokens,
      }
    : null;

// ----------------------------------------------------------------------

export const completeChat = async (options: {
  model: string;
  messages: ChatMessage[];
  tools?: ChatTool[];
  temperature?: number;
  signal?: AbortSignal;
}): Promise<ChatCompletionResult> => {
  const { model, tools, temperature = LLM_TEMPERATURE, signal } = options;

  const { messages, counts } = redactResidual(options.messages);
  if (Object.keys(counts).length) {
    trackPrivacyResidualRedacted(counts);
  }

  const response = await axios.post<CommonResponse<ChatCompletion>>(
    ENDPOINTS.ai.chatCompletions,
    {
      model,
      messages,
      temperature,
      ...(tools?.length ? { tools, tool_choice: 'auto' as const } : {}),
    },
    { signal }
  );

  if (isErrorResponse(response.data)) {
    if (response.data.code === AI_ERROR_CODES.CONTEXT_LENGTH_EXCEEDED) {
      throw new ContextOverflowError();
    }
    throw new Error(`The LLM provider request failed (${response.status}): ${response.data.code}`);
  }

  const message = response.data?.choices?.[0]?.message;
  if (!message) {
    throw new Error('Empty completion from the LLM provider');
  }

  return { message, usage: toUsage(response.data.usage) };
};
